import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Camera, Video, Download, RefreshCw,
  CheckCircle, ExternalLink, Sparkles, Search, Loader2
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import VideoHistory from './VideoHistory';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ProgressBar = ({ progress }) => (
  <div className="w-full space-y-2">
    <div className="flex justify-between text-xs text-gray-500">
      <span>Generating your video...</span>
      <span>{progress}%</span>
    </div>
    <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden">
      <div
        className="h-3 rounded-full bg-gradient-to-r from-pink-500 to-rose-500 transition-all duration-1000"
        style={{ width: `${progress}%` }}
      />
    </div>
  </div>
);

const PhotoAvatarPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1=pick photo, 2=script, 3=generating, 4=done
  const [talkingPhotos, setTalkingPhotos] = useState([]);
  const [filteredPhotos, setFilteredPhotos] = useState([]);
  const [loadingPhotos, setLoadingPhotos] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 100;
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [voices, setVoices] = useState([]);
  const [selectedVoice, setSelectedVoice] = useState('');
  const [script, setScript] = useState('');
  const [videoUrl, setVideoUrl] = useState(null);
  const [statusMsg, setStatusMsg] = useState('');
  const [progress, setProgress] = useState(0);
  const [errorMsg, setErrorMsg] = useState('');
  const [recordId, setRecordId] = useState(null);

  const getToken = () => localStorage.getItem('token');

  useEffect(() => {
    loadPhotos();
    loadVoices();
  }, []);

  const loadPhotos = async () => {
    const token = getToken();
    if (!token) { setLoadingPhotos(false); return; }
    try {
      const res = await axios.get(`${API}/heygen/talking-photos`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const photos = res.data.talking_photos || [];
      setTalkingPhotos(photos);
      setFilteredPhotos(photos); // store all, pagination handles slicing
    } catch (_) {
      toast.error('Failed to load photo gallery');
    }
    setLoadingPhotos(false);
  };

  useEffect(() => {
    setPage(0);
    if (!searchQuery.trim()) {
      setFilteredPhotos(talkingPhotos);
    } else {
      const q = searchQuery.toLowerCase();
      setFilteredPhotos(talkingPhotos.filter(p => p.id.includes(q)));
    }
  }, [searchQuery, talkingPhotos]);

  const loadVoices = async () => {
    try {
      const res = await axios.get(`${API}/heygen/voices`);
      const vs = res.data.voices || [];
      setVoices(vs);
      if (vs.length > 0) setSelectedVoice(vs[0].voice_id);
    } catch (_) {}
  };

  const startProgress = () => {
    setProgress(10);
    const steps = [
      { at: 5000, val: 20 }, { at: 15000, val: 35 }, { at: 30000, val: 50 },
      { at: 60000, val: 65 }, { at: 90000, val: 80 }, { at: 120000, val: 90 },
    ];
    steps.forEach(s => setTimeout(() => setProgress(v => Math.max(v, s.val)), s.at));
  };

  const generate = async () => {
    if (!script.trim()) { toast.error('Please write a script'); return; }
    const token = getToken();
    if (!token) { toast.error('Please sign in'); return; }

    setStep(3); setStatusMsg('Submitting your request...'); setProgress(5); setErrorMsg('');

    try {
      const res = await axios.post(`${API}/heygen/photo-avatar-video`, {
        talking_photo_id: selectedPhoto.id,
        voice_id: selectedVoice,
        script,
        title: 'Photo Avatar Video'
      }, { headers: { Authorization: `Bearer ${token}` } });

      if (res.data.success && res.data.video_id) {
        setRecordId(res.data.record_id);
        setStatusMsg('Video queued — AI is generating it...');
        startProgress();
        pollVideo(res.data.video_id, res.data.record_id);
      }
    } catch (e) {
      setStep(2);
      setErrorMsg(e.response?.data?.detail || 'Generation failed. Try again.');
    }
  };

  const pollVideo = (id, recId) => {
    let n = 0;
    const t = setInterval(async () => {
      n++;
      if (n > 120) { clearInterval(t); setStep(2); setErrorMsg('Timed out. Try again.'); return; }
      try {
        const res = await axios.get(`${API}/heygen/video-status/${id}`);
        const { status, video_url, thumbnail_url } = res.data;
        if (status === 'completed' && video_url) {
          clearInterval(t); setProgress(100); setVideoUrl(video_url); setStep(4);
          toast.success('Your photo avatar video is ready!');
          if (recId) {
            const token = getToken();
            axios.patch(`${API}/heygen/video-record/${recId}`,
              { status: 'completed', video_url, thumbnail_url },
              { headers: { Authorization: `Bearer ${token}` } }
            ).catch(() => {});
          }
        } else if (status === 'failed') {
          clearInterval(t); setStep(2); setErrorMsg('Generation failed. Try again.');
          if (recId) {
            const token = getToken();
            axios.patch(`${API}/heygen/video-record/${recId}`,
              { status: 'failed' },
              { headers: { Authorization: `Bearer ${token}` } }
            ).catch(() => {});
          }
        } else {
          setStatusMsg(`Processing... (${status})`);
          setProgress(v => Math.max(v, Math.min(93, 10 + n * 1.5)));
        }
      } catch (_) {}
    }, 5000);
  };

  const reset = () => {
    setStep(1); setSelectedPhoto(null); setScript(''); setVideoUrl(null);
    setProgress(0); setErrorMsg(''); setRecordId(null);
  };

  const handleDownload = () => {
    const a = document.createElement('a');
    a.href = videoUrl; a.download = 'photo-avatar-video.mp4'; a.target = '_blank';
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  // Build image URL with fallback
  const getPhotoUrl = (p) => {
    if (!p.image_url) return null;
    // image_url might be partial; add jpg extension if needed
    const url = p.image_url;
    if (url.startsWith('http')) return url;
    return `https://files2.heygen.ai/talking_photo/${p.id}/${url}.jpg`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-rose-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-2xl mx-auto px-4 py-3">
          <button
            onClick={() => navigate('/creator-ai/heygen-hub')}
            className="flex items-center gap-1.5 text-gray-500 hover:text-gray-900 mb-1.5 text-xs sm:text-sm"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back
          </button>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 bg-gradient-to-br from-pink-500 to-rose-500 rounded-xl flex items-center justify-center flex-shrink-0">
                <Camera className="h-4 w-4 text-white" />
              </div>
              <div>
                <h1 className="text-base font-bold text-gray-900">Photo Avatar</h1>
                <p className="text-xs text-gray-500">Make a photo speak your script · 200 credits</p>
              </div>
            </div>
            {step < 3 && (
              <div className="flex items-center gap-1">
                {['Photo', 'Script'].map((label, idx) => (
                  <React.Fragment key={idx}>
                    <div className="flex items-center gap-1">
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center text-xs font-bold ${
                        step === idx + 1 ? 'bg-pink-600 text-white' : idx + 1 < step ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500'
                      }`}>{idx + 1 < step ? '✓' : idx + 1}</div>
                      <span className="hidden sm:inline text-xs text-gray-500">{label}</span>
                    </div>
                    {idx < 1 && <div className={`w-3 sm:w-5 h-0.5 ${idx + 1 < step ? 'bg-green-400' : 'bg-gray-200'}`} />}
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4 py-4 space-y-4">

        {/* Video History at top */}
        {step === 1 && <VideoHistory videoType="photo_avatar" accentColor="pink" />}

        {/* STEP 1 — Pick a talking photo */}
        {step === 1 && (
          <div className="space-y-3">
            <div className="text-center">
              <h2 className="text-xl font-bold text-gray-900 mb-1">Choose a Talking Photo</h2>
              <p className="text-gray-500 text-xs sm:text-sm">Pick a photo character to speak your script</p>
            </div>

            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search by name or gender..."
                className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-pink-400"
                data-testid="photo-search-input"
              />
            </div>

            {loadingPhotos ? (
              <div className="flex items-center justify-center py-10 text-gray-400 gap-2">
                <Loader2 className="h-5 w-5 animate-spin" />
                <span className="text-sm">Loading photo gallery...</span>
              </div>
            ) : (
              <>
                {/* Page info + top nav */}
                <div className="flex items-center justify-between mb-2">
                  <p className="text-xs text-gray-500">
                    {page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, filteredPhotos.length)} of {filteredPhotos.length}
                  </p>
                  <div className="flex gap-1.5">
                    <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
                      className="text-xs px-2.5 py-1.5 rounded-lg border border-gray-200 disabled:opacity-30 hover:bg-gray-50 font-medium">
                      ← Prev
                    </button>
                    <span className="text-xs px-2 py-1.5 text-gray-400">pg {page + 1}/{Math.ceil(filteredPhotos.length / PAGE_SIZE)}</span>
                    <button onClick={() => setPage(p => p + 1)} disabled={(page + 1) * PAGE_SIZE >= filteredPhotos.length}
                      className="text-xs px-2.5 py-1.5 rounded-lg border border-gray-200 disabled:opacity-30 hover:bg-gray-50 font-medium">
                      Next →
                    </button>
                  </div>
                </div>

                {/* Photo grid */}
                <div className="grid grid-cols-4 sm:grid-cols-5 gap-2 max-h-[420px] overflow-y-auto pr-1">
                  {filteredPhotos.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE).map(photo => {
                    const imgUrl = getPhotoUrl(photo);
                    const isSelected = selectedPhoto?.id === photo.id;
                    return (
                      <button
                        key={photo.id}
                        onClick={() => setSelectedPhoto(photo)}
                        data-testid={`talking-photo-${photo.id}`}
                        className={`relative w-full aspect-square block rounded-xl overflow-hidden border-2 transition-all bg-gray-100 ${
                          isSelected
                            ? 'border-pink-500 ring-2 ring-pink-300'
                            : 'border-gray-200 hover:border-pink-300'
                        }`}
                      >
                        {imgUrl && (
                          <img
                            src={imgUrl}
                            alt="Avatar"
                            loading="lazy"
                            className="absolute inset-0 w-full h-full object-cover object-top"
                            onError={e => { e.currentTarget.style.display = 'none'; }}
                          />
                        )}
                        {isSelected && (
                          <div className="absolute inset-0 bg-pink-500/30 flex items-end justify-center pb-1">
                            <CheckCircle className="h-5 w-5 text-white drop-shadow" />
                          </div>
                        )}
                      </button>
                    );
                  })}
                </div>

                {/* Bottom nav */}
                <div className="flex items-center justify-between mt-2">
                  <button onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}
                    className="text-xs px-3 py-2 rounded-xl border border-gray-200 disabled:opacity-30 hover:bg-gray-50 font-medium">
                    ← Previous 100
                  </button>
                  <button onClick={() => setPage(p => p + 1)} disabled={(page + 1) * PAGE_SIZE >= filteredPhotos.length}
                    className="text-xs px-3 py-2 rounded-xl border border-gray-200 disabled:opacity-30 hover:bg-gray-50 font-medium">
                    Next 100 →
                  </button>
                </div>
              </>
            )}

            {selectedPhoto && (
              <div className="bg-pink-50 border border-pink-200 rounded-2xl p-3 flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl overflow-hidden border-2 border-pink-300 flex-shrink-0">
                  <img src={getPhotoUrl(selectedPhoto)} alt="Selected" className="w-full h-full object-cover"
                    onError={e => { e.target.style.display = 'none'; }} loading="lazy" decoding="async" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-pink-900 text-sm">Avatar selected!</p>
                  <p className="text-xs text-pink-700 truncate">ID: {selectedPhoto.id.slice(0, 12)}...</p>
                </div>
              </div>
            )}

            <button
              onClick={() => { if (!selectedPhoto) { toast.error('Please select a photo'); return; } setStep(2); }}
              disabled={!selectedPhoto}
              className="w-full bg-gradient-to-r from-pink-600 to-rose-500 text-white font-semibold py-3.5 rounded-xl disabled:opacity-40 flex items-center justify-center gap-2 text-sm"
              data-testid="photo-avatar-next-btn"
            >
              Next: Write Script →
            </button>
          </div>
        )}

        {/* STEP 2 — Script */}
        {step === 2 && (
          <div className="space-y-4">
            <div className="text-center">
              <h2 className="text-xl font-bold text-gray-900 mb-1">Write Your Script</h2>
              <p className="text-xs text-gray-500">Your selected photo will speak this text</p>
            </div>

            {selectedPhoto && (
              <div className="flex items-center gap-3 bg-pink-50 border border-pink-200 rounded-2xl p-3">
                <div className="w-14 h-14 rounded-xl overflow-hidden border-2 border-pink-300 flex-shrink-0">
                  <img src={getPhotoUrl(selectedPhoto)} alt="Photo" className="w-full h-full object-cover"
                    onError={e => { e.target.style.display = 'none'; }} loading="lazy" decoding="async" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 text-sm">Photo ready</p>
                  <p className="text-xs text-gray-500">Your chosen photo will be animated</p>
                </div>
                <button onClick={() => setStep(1)} className="text-xs text-pink-600 underline flex-shrink-0">Change</button>
              </div>
            )}

            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Voice</label>
                <select
                  value={selectedVoice} onChange={e => setSelectedVoice(e.target.value)}
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-pink-400 bg-white"
                >
                  {voices.map(v => (
                    <option key={v.voice_id} value={v.voice_id}>
                      {v.display_name} {v.locale ? `(${v.locale})` : ''} {v.gender ? `· ${v.gender}` : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">Script</label>
                <textarea
                  value={script} onChange={e => setScript(e.target.value)} rows={5}
                  placeholder="Type what your photo should say...\n\nExample: Hi! I'm excited to share this amazing product with you today!"
                  className="w-full border border-gray-300 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-pink-400 resize-none"
                  data-testid="photo-avatar-script-input"
                />
                <p className="text-xs text-gray-400 mt-1">{script.length} chars — recommended 30–500</p>
              </div>

              {errorMsg && <p className="text-xs text-red-500 bg-red-50 rounded-xl px-4 py-3">{errorMsg}</p>}

              <div className="flex flex-col gap-3">
                <button
                  onClick={generate} disabled={!script.trim()}
                  className="w-full bg-gradient-to-r from-pink-600 to-rose-500 text-white font-semibold py-3.5 rounded-xl disabled:opacity-40 flex items-center justify-center gap-2 text-sm"
                  data-testid="photo-avatar-generate-btn"
                >
                  <Video className="h-4 w-4" /> Generate Video (200 credits)
                </button>
                <button onClick={() => setStep(1)} className="w-full border-2 border-gray-300 text-gray-700 font-semibold py-3 rounded-xl text-sm">
                  ← Back to Photo Selection
                </button>
              </div>
            </div>
          </div>
        )}

        {/* STEP 3 — Generating */}
        {step === 3 && (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 space-y-5">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-rose-500 rounded-xl flex items-center justify-center flex-shrink-0 animate-pulse">
                <Sparkles className="h-6 w-6 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-gray-900 text-sm">Animating Your Photo</p>
                <p className="text-xs text-gray-500 mt-0.5">{statusMsg}</p>
              </div>
            </div>
            <ProgressBar progress={progress} />
            <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
              <p className="text-xs text-amber-800">⏱ Average time: <strong>1–3 minutes</strong>. Keep this page open.</p>
            </div>
          </div>
        )}

        {/* STEP 4 — Done */}
        {step === 4 && videoUrl && (
          <div className="bg-white rounded-2xl border border-green-200 shadow-sm p-4 sm:p-6 space-y-4">
            <div className="flex items-center justify-center gap-2 text-green-600">
              <CheckCircle className="h-5 w-5" />
              <span className="font-bold text-base">Your Photo Avatar Video is Ready!</span>
            </div>
            <div className="bg-black rounded-xl overflow-hidden">
              <video
                src={videoUrl} controls muted playsInline
                className="w-full max-h-[300px] sm:max-h-[420px]"
                data-testid="photo-avatar-result-video"
              />
            </div>
            <div className="flex flex-col gap-3">
              <button
                onClick={handleDownload}
                className="w-full bg-gradient-to-r from-pink-600 to-rose-500 text-white font-semibold py-3.5 rounded-xl flex items-center justify-center gap-2 text-sm"
                data-testid="photo-avatar-download-btn"
              >
                <Download className="h-4 w-4" /> Download Video
              </button>
              <a
                href={videoUrl} target="_blank" rel="noreferrer"
                className="w-full border-2 border-pink-300 text-pink-700 font-semibold py-3.5 rounded-xl flex items-center justify-center gap-2 hover:bg-pink-50 text-sm"
              >
                <ExternalLink className="h-4 w-4" /> Open in Browser
              </a>
              <button
                onClick={reset}
                className="w-full border-2 border-gray-200 text-gray-600 font-semibold py-3 rounded-xl flex items-center justify-center gap-2 text-sm"
              >
                <RefreshCw className="h-4 w-4" /> Create Another
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default PhotoAvatarPage;
